import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useMqttStore } from '@/stores/mqtt'
import { useUserStore } from '@/stores/user'
import { TRUSTED_PARENT_ORIGINS } from '@/constants/trusted-origins'
import { parseJSON } from '@/utils'
import type { IArchivedMessageConfig, IConfig, IProfile, IProject } from '@/interfaces/gui'
import { currentRoute, useCustomRouter } from '@/composables/useCustomRouter'
import { circuitOfRoute, circuitPageRoute, startRoute } from '@/circuits/circuits'
import { pickSystemForStatus, readStartIntent } from '@/composables/useStartIntent'
import { ALL_CIRCUITS } from '@/messages/messages'
import { useUiStore } from '@/stores/ui'
import type { ITimerResponse, ITimerData, IEventTimerResponse } from '@/interfaces/timer'
import type { ITrendResponse } from '@/interfaces/trend'

const getParentOrigin = (): string | null => {
  if (!document.referrer) {
    return null
  }
  try {
    return new URL(document.referrer).origin
  } catch {
    return null
  }
}

export const isLoading = ref(true)
export const config = ref({} as IConfig)
export const project = ref({} as IProject)
export const contact = ref({} as IProfile)
export const archivedMessages = ref([] as IArchivedMessageConfig[])
export const isLoadingMore = ref(false)

interface ArchiveRequest {
  readonly system: string
  readonly date: number
}

/** A day's archive the device has been asked for; its answer carries no system of its own. */
let archiveInFlight: ArchiveRequest | undefined
let archiveQueue: ArchiveRequest[] = []
let archiveTimeout: ReturnType<typeof setTimeout> | undefined
const ARCHIVE_TIMEOUT_MS = 15_000
export const currentTimer = ref({} as ITimerResponse | IEventTimerResponse)
export const trend = ref({} as ITrendResponse)
export const isOffline = ref(false)

// Screenshot data
export const screenshotData = ref('')
export const isLoadingScreenshot = ref(false)

// Authentication data received from parent window
export const authData = ref({
  access_token: '',
  device_identifier: '',
  session_id: '',
})

const START_INTENT_WAIT_MS = 2000
const START_INTENT_POLL_MS = 200

/**
 * Handles `?open=messages&status=<status>` from the dinoConnect app. The start route stays in the
 * history underneath, so "back" leads to the overview. If the circuit cannot be determined within
 * START_INTENT_WAIT_MS (datapoints still arriving), the message page shows all circuits.
 */
async function openMessagesFromStartIntent() {
  const intent = readStartIntent()
  const systems = config.value.systems ?? []
  if (!intent || systems.length === 0) return

  const mqttStore = useMqttStore()
  const getDatapointValue = (datapoint: string) =>
    mqttStore.getValue(`${getDeviceIdentifier()}/datapoints/${datapoint}`)
  const messages = config.value.messages ?? []

  let systemName = pickSystemForStatus(systems, messages, intent.status, getDatapointValue)
  for (let waited = 0; !systemName && waited < START_INTENT_WAIT_MS; waited += START_INTENT_POLL_MS) {
    await new Promise((resolve) => setTimeout(resolve, START_INTENT_POLL_MS))
    systemName = pickSystemForStatus(systems, messages, intent.status, getDatapointValue)
  }

  // A message list of its own (a Yellow's Bluewave) is no circuit; its page opens in the circuit in force
  const { navigateTo } = useCustomRouter()
  const circuit =
    systems.find((s) => s.name === systemName) ?? circuitOfRoute(currentRoute.value, systems) ?? systems[0]
  useUiStore().requestMessageScope(systemName ?? ALL_CIRCUITS)
  navigateTo(circuitPageRoute(circuit, 'messagePage'))
}

// Get device identifier from authData
export const getDeviceIdentifier = (): string => {
  return authData.value.device_identifier
}

// Check if authentication data is valid
export const hasValidAuthData = (): boolean => {
  return !!authData.value.access_token && !!authData.value.device_identifier
}

// Helper function to handle MQTT authentication and connection
const setMqttAuthValues = (token: string, deviceId: string, sessionId: string) => {
  // Update authentication data
  authData.value.access_token = token
  authData.value.device_identifier = deviceId
  authData.value.session_id = sessionId
}

// Setup cross-origin communication
const setupMessageListener = () => {
  const messageHandler = async (event: MessageEvent) => {
    if (import.meta.env.DEV) {
      console.log('📨 Received message:', {
        origin: event.origin,
        data: event.data,
        trustedOrigins: TRUSTED_PARENT_ORIGINS,
      })
    }

    if (event.source !== window.parent) {
      return
    }

    if (!TRUSTED_PARENT_ORIGINS.includes(event.origin)) {
      console.warn(
        `🚫 Received message from untrusted origin: ${event.origin}. Trusted origins:`,
        TRUSTED_PARENT_ORIGINS,
      )
      return
    }

    if (import.meta.env.DEV) {
      console.log('✅ Message from trusted origin:', event.origin)
    }

    // Process the authentication data
    if (event.data && typeof event.data === 'object' && event.data.type === 'AUTH_DATA') {
      if (import.meta.env.DEV) {
        console.log('🔐 Received authentication data from parent:', {
          hasAccessToken: !!event.data.access_token,
          hasDeviceIdentifier: !!event.data.device_identifier,
          hasSessionId: !!event.data.session_id,
          deviceIdentifier: event.data.device_identifier,
        })
      }

      if (event.data.access_token && event.data.device_identifier && event.data.session_id) {
        // Check for sleep parameter and delay if present
        const sleepSeconds = window.sleepSeconds
        if (sleepSeconds) {
          const sleepDuration = parseInt(sleepSeconds, 10)
          if (!isNaN(sleepDuration) && sleepDuration > 0) {
            if (import.meta.env.DEV) {
              console.log(`😴 Sleeping for ${sleepDuration} seconds after receiving auth params...`)
            }
            await new Promise((resolve) => setTimeout(resolve, sleepDuration * 1000))
            if (import.meta.env.DEV) {
              console.log('😴 Sleep completed, continuing with auth processing')
            }
          }
        }

        setMqttAuthValues(
          event.data.access_token,
          event.data.device_identifier,
          event.data.session_id,
        )
        if (import.meta.env.DEV) {
          console.log('✅ Auth values set successfully')
        }

        // Update MQTT store to indicate auth data is available
        const mqttStore = useMqttStore()
        mqttStore.hasAuthData = true
      } else {
        console.error('❌ Missing required auth data:', {
          access_token: !!event.data.access_token,
          device_identifier: !!event.data.device_identifier,
          session_id: !!event.data.session_id,
        })
      }

      // Acknowledge receipt of authentication data
      if (import.meta.env.DEV) {
        console.log('📤 Sending AUTH_RECEIVED response to parent')
      }
      event.source?.postMessage(
        {
          type: 'AUTH_RECEIVED',
          success: true,
        },
        { targetOrigin: event.origin },
      )
      if (import.meta.env.DEV) {
        console.log('✅ AUTH_RECEIVED response sent')
      }
    } else if (import.meta.env.DEV) {
      console.log('ℹ️ Received non-auth message:', event.data)
    }
  }

  window.addEventListener('message', messageHandler)

  return () => {
    window.removeEventListener('message', messageHandler)
  }
}

// Initialize MQTT connection with credentials
export function initializeMqttConnection(token: string, deviceId: string, sessionId: string): void {
  setMqttAuthValues(token, deviceId, sessionId)
}

// Helper function to filter out elements with remote: false

export function filterRemoteElements<T extends object>(obj: T): T | null {
  // If remote is explicitly false, filter out this element
  if (obj && typeof obj === 'object' && 'remote' in obj && !(obj as { remote?: boolean }).remote) {
    return null
  }

  if (!obj || typeof obj !== 'object') {
    return obj
  }

  // Create a new object to avoid mutating the input
  const filtered = { ...obj }

  // Handle arrays and objects
  for (const key in filtered) {
    const value = filtered[key]

    if (Array.isArray(value)) {
      const filteredArray = value
        .map((item) => (item && typeof item === 'object' ? filterRemoteElements(item) : item))
        .filter((item): item is NonNullable<typeof item> => item !== null)

      if (filteredArray.length === 0) {
        delete filtered[key]
      } else {
        filtered[key] = filteredArray as unknown as T[Extract<keyof T, string>]
      }
    }
    // Handle nested objects
    else if (value && typeof value === 'object') {
      const filteredValue = filterRemoteElements(value)
      if (filteredValue === null) {
        delete filtered[key]
      } else {
        filtered[key] = filteredValue as unknown as T[Extract<keyof T, string>]
      }
    }
  }

  return filtered as T
}

// Extract setupSubscriptions to be accessible outside useMqttConnection
const setupSubscriptions = () => {
  const mqttStore = useMqttStore()
  const userStore = useUserStore()

  mqttStore.subscribe(`${getDeviceIdentifier()}/status`, (value) => {
    isOffline.value = value === 'offline'
  })

  // Add subscription for login acknowledgment
  mqttStore.subscribe(`${getDeviceIdentifier()}/ack`, (value) => {
    try {
      // Handle the specific WebSocket response format
      let response: Record<string, unknown>

      // Check if we need to parse the payload string
      if (typeof value === 'string') {
        response = parseJSON(value) as Record<string, unknown>
      } else if (typeof value === 'object' && value !== null) {
        // If it's already an object but has a payload string that needs parsing
        const valueObj = value as Record<string, unknown>
        if (typeof valueObj.payload === 'string') {
          response = parseJSON(valueObj.payload as string) as Record<string, unknown>
        } else {
          response = valueObj
        }
      } else {
        return
      }

      // Now we can check if this is a login response
      if (response.cmd === 'login') {
        if (response.status === 'success') {
          // Update user store with login information
          userStore.setUserInfo(
            response.name as string,
            Number(response.level),
            response.session as string,
          )
          // Dispatch success event
          window.dispatchEvent(
            new CustomEvent('login-result', {
              detail: { success: true },
            }),
          )
        } else if (response.status === 'error') {
          // Dispatch error event with message
          window.dispatchEvent(
            new CustomEvent('login-result', {
              detail: {
                success: false,
                errorMsg: (response.errorMsg as string) || 'login_failed',
              },
            }),
          )
        }
      }
    } catch (error) {
      console.error('Error processing ack message:', error)
    }
  })

  mqttStore.subscribe(`${getDeviceIdentifier()}/project`, async (value) => {
    console.log(`${getDeviceIdentifier()}/project`, value)
    const parsedValue = parseJSON<IProject>(JSON.stringify(value))
    if (parsedValue) {
      // Filter out elements with remote: false before assigning
      // const filteredProject = parsedValue
      const filteredProject = filterRemoteElements(parsedValue)
      if (filteredProject) {
        project.value = filteredProject
        config.value = filteredProject.config

        // Update contact information from project data
        if (filteredProject.profile) {
          contact.value = filteredProject.profile
        }

        // Create a one-time watcher that will trigger after the config is fully updated
        const unwatch = watch(
          () => config.value,
          async (newConfig) => {
            if (newConfig && newConfig.systems?.length > 0 && isLoading.value) {
              await nextTick()
              const start = startRoute(newConfig.systems)
              if (start) {
                // Fetched here, not at load: the router imports this module in turn.
                useCustomRouter().navigateTo(start)
              }
              isLoading.value = false
              unwatch() // Clean up the watcher since we only need it once
              // Opened from a push notification: continue to the message page of the affected circuit
              await openMessagesFromStartIntent()
            }
          },
          { immediate: true },
        )
      }
    } else {
      console.error('Failed to parse GUI data')
    }
  })

  mqttStore.subscribe(`${getDeviceIdentifier()}/messages`, (value) => {
    if (Array.isArray(value)) receiveArchivedMessages(value as IArchivedMessageConfig[])
  })

  mqttStore.subscribe(`${getDeviceIdentifier()}/timer`, (value) => {
    const parsedValue = parseJSON<ITimerResponse>(JSON.stringify(value))
    if (parsedValue) {
      if (import.meta.env.DEV) {
        console.log('Timer data received:', parsedValue)
      }
      currentTimer.value = parsedValue
    } else {
      console.error('Failed to parse timer data')
    }
  })

  // Add subscription for screenshot data
  mqttStore.subscribe(`${getDeviceIdentifier()}/screen`, (value) => {
    // Handle the data directly without JSON parsing, as it may be a base64 string
    screenshotData.value = value as string
    isLoadingScreenshot.value = false
  })

  mqttStore.subscribe(`${getDeviceIdentifier()}/dmx`, (value) => {
    const parsedValue = parseJSON<IDmxState>(JSON.stringify(value))
    if (parsedValue) {
      currentDmx.value = parsedValue
    } else {
      console.error('Failed to parse DMX data')
    }
  })

  mqttStore.subscribe(`${getDeviceIdentifier()}/trend`, (value) => {
    const parsedValue = parseJSON<ITrendResponse>(JSON.stringify(value))
    if (parsedValue) {
      if (import.meta.env.DEV) {
        console.log('Trend data received:', parsedValue)
      }
      trend.value = parsedValue
    } else {
      console.error('Failed to parse timer data')
    }
  })
}

export function useMqttConnection() {
  const mqttStore = useMqttStore()

  let cleanupMessageListener: (() => void) | null = null

  onMounted(() => {
    cleanupMessageListener = setupMessageListener()
    // Send ready signal to parent window
    if (window.parent !== window) {
      if (import.meta.env.DEV) {
        console.log('📤 Sending IFRAME_READY signal to parent')
      }

      const parentOrigin = getParentOrigin()

      if (parentOrigin && TRUSTED_PARENT_ORIGINS.includes(parentOrigin)) {
        window.parent.postMessage(
          {
            type: 'IFRAME_READY',
            ready: true,
          },
          parentOrigin,
        )
        if (import.meta.env.DEV) {
          console.log('✅ IFRAME_READY signal sent to:', parentOrigin)
        }
      }
    } else if (import.meta.env.DEV) {
      console.log('ℹ️ Not in iframe context, skipping IFRAME_READY signal')
    }

    // Watch for valid authentication data
    watch(
      () => [
        authData.value.access_token,
        authData.value.device_identifier,
        authData.value.session_id,
      ],
      (newValues, oldValues) => {
        const [newToken, newDeviceId, newSessionId] = newValues
        const oldDeviceId = oldValues ? oldValues[1] : undefined

        if (newToken && newDeviceId) {
          if (import.meta.env.DEV) {
            console.log('🔗 Valid auth data available, attempting MQTT connection:', {
              hasToken: !!newToken,
              deviceId: newDeviceId,
              isConnected: mqttStore.isConnected,
            })
          }

          // If we have valid auth data and aren't connected, connect
          if (!mqttStore.isConnected) {
            if (import.meta.env.DEV) {
              console.log('🚀 Connecting to MQTT with auth data')
            }
            mqttStore.connect({
              device_identifier: newDeviceId,
              access_token: newToken,
              session_id: newSessionId || '',
            })
          } else if (import.meta.env.DEV) {
            console.log('ℹ️ Already connected to MQTT')
          }

          // Setup subscriptions if device ID changed or is newly available
          if (newDeviceId !== oldDeviceId) {
            if (import.meta.env.DEV) {
              console.log('📡 Setting up subscriptions for device:', newDeviceId)
            }
            setupSubscriptions()
          }
        } else if (import.meta.env.DEV) {
          console.log('⏳ Waiting for valid auth data:', {
            hasToken: !!newToken,
            hasDeviceId: !!newDeviceId,
            hasSessionId: !!newSessionId,
          })
        }
      },
      { immediate: true },
    )

    // Once connected, send the sendAll command (should be handled server-side)
    // const stopWatch = watch(
    //   () => mqttStore.isConnected,
    //   (isConnected) => {
    //     if (isConnected && hasValidAuthData()) {
    //       mqttStore.publish(`${getDeviceIdentifier()}/cmd`, { cmd: 'sendAll' })
    //       stopWatch() // Stop watching after the command is sent
    //     }
    //   }
    // )
  })

  onUnmounted(() => {
    cleanupMessageListener?.()
    mqttStore.disconnect()
  })
}

export function setDatapoint(
  datapointId: string,
  value: string | number | boolean | null | undefined,
): void {
  if (typeof value === 'undefined' || value === null) {
    return
  }
  /*
  // Get datapoint configuration to check min/max values
  const datapointConfig = config.value.datapoints[datapointId]

  // Only check range for numeric values
  if (typeof value === 'number' && datapointConfig) {
    // Check if min value exists and value is below it
    if (datapointConfig.min !== undefined && value < datapointConfig.min) {
      console.warn(`Value ${value} is below minimum ${datapointConfig.min} for datapoint ${datapointId}`)
      return
    }

    // Check if max value exists and value is above it
    if (datapointConfig.max !== undefined && value > datapointConfig.max) {
      console.warn(`Value ${value} is above maximum ${datapointConfig.max} for datapoint ${datapointId}`)
      return
    }
  }
*/
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'setDP',
    dp: datapointId,
    value: value,
    ackRequired: false, // optional
  })
}

/**
 * Attraction commands carry the *intent* only. The ~300 ms colour pulse runs
 * entirely on the device, so a slow or dropping connection can no longer
 * stretch it into the wrong colour programme or leave the light switched off
 * because the second packet never arrived.
 *
 * The `hold_*` actions are the push mode: the device runs the output only while
 * heartbeats keep arriving, so a dropped connection releases it instead of
 * leaving it switched on.
 */
export type AttractionAction = 'set' | 'colorstep' | 'hold_start' | 'hold_beat' | 'hold_stop'

/** Devices running the attraction engine announce it in the project features. */
export function supportsAttractionEngine(): boolean {
  return project.value.features?.includes('attraction_v1') ?? false
}

export function sendAttractionCommand(
  target: string,
  action: AttractionAction,
  value?: boolean,
): void {
  const mqttStore = useMqttStore()
  const payload: Record<string, unknown> = {
    cmd: 'attraction',
    target,
    action,
    session: mqttStore.sessionId,
    // Failures are acknowledged either way; this asks for the success ack too.
    // Not for heartbeats: two a second, each one a packet back, and the only
    // answer that matters — not_holding — arrives without asking.
    ackRequired: action !== 'hold_beat',
  }

  if (action === 'set') {
    // Must be a real JSON boolean — 1, "1" and "true" are rejected.
    payload.value = value === true
  }

  // A lost heartbeat is replaced by the next one 500 ms later, so QoS 0 keeps it
  // off the retransmit queue — on a bad line that queue is what delays the beats
  // that still matter. Everything else has to arrive; a duplicate is harmless
  // because the device keeps no sequence state and every value is absolute.
  //
  // The bridge adds remoteUser/localUser for the device's change log.
  mqttStore.publish(
    `${getDeviceIdentifier()}/cmd/attraction`,
    payload,
    action === 'hold_beat' ? 0 : 1,
  )
}

/**
 * A full re-dump of the project is expensive, and every attraction card that hits
 * the same stale configuration would ask for one. Throttling here rather than per
 * card is what keeps a mis-typed target from turning into a request per press.
 */
const CONFIG_RELOAD_THROTTLE_MS = 30000
let lastConfigReload = 0

/**
 * Asks the device to send its configuration again.
 *
 * `sendAll` is what the bridge itself sends on connect, so the answer arrives on
 * the topics the app is already subscribed to — nothing here has to wait for it.
 *
 * Returns false when the request was swallowed by the throttle.
 */
export function requestConfigReload(): boolean {
  const now = Date.now()
  if (now - lastConfigReload < CONFIG_RELOAD_THROTTLE_MS) {
    return false
  }
  lastConfigReload = now

  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, { cmd: 'sendAll' })
  return true
}

/**
 * Asks the device for the archive of the given circuits on the day of `date`,
 * one circuit after the other: the device answers each request with a bare
 * list, so only the request in flight tells whose messages arrive. A new call
 * replaces the circuits still waiting; a request left unanswered ends the
 * queue, so a late answer is dropped instead of filed under the next circuit.
 */
export function fetchArchivedMessages(systems: ReadonlyArray<string>, date: Date): void {
  archiveQueue = systems.map((system) => ({ system, date: date.getTime() }))
  if (!archiveInFlight) requestNextArchive()
}

function requestNextArchive(): void {
  clearTimeout(archiveTimeout)
  archiveInFlight = archiveQueue.shift()
  isLoadingMore.value = archiveInFlight !== undefined
  if (!archiveInFlight) return

  useMqttStore().publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'getMessages',
    system: archiveInFlight.system,
    date: archiveInFlight.date,
  })
  archiveTimeout = setTimeout(abandonArchive, ARCHIVE_TIMEOUT_MS)
}

function abandonArchive(): void {
  archiveQueue = []
  archiveInFlight = undefined
  isLoadingMore.value = false
}

/** Files the device's answer under the circuit asked for; an answer nobody here asked for is dropped. */
export function receiveArchivedMessages(received: ReadonlyArray<IArchivedMessageConfig>): void {
  const request = archiveInFlight
  if (!request) return

  const key = (message: IArchivedMessageConfig) =>
    `${message.system}_${message.start}_${message.type}_${message.text}`
  const known = new Set(archivedMessages.value.map(key))
  const fresh = received
    .map((message) => ({ ...message, system: request.system }))
    .filter((message) => !known.has(key(message)))
  if (fresh.length > 0) archivedMessages.value = [...archivedMessages.value, ...fresh]

  requestNextArchive()
}

export function getTimer(timerId: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'getTimer',
    id: timerId,
    session: sessionId,
  })
}

export function setTimer(timerId: string, data: ITimerData, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'setTimer',
    id: timerId,
    data: data,
    session: sessionId,
  })
}

// Add new file management functions
export function prepareFileUpload(filename: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'prepareUpload',
    filename: filename,
    session: sessionId,
  })
}

export function downloadFile(filename: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'downloadFile',
    filename: filename,
    session: sessionId,
  })
}

export function moveFile(oldPath: string, newPath: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'moveFile',
    filename: oldPath,
    destination: newPath,
    session: sessionId,
    ackRequired: true,
  })
}

export function listFiles(path: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'listFiles',
    path: path,
    session: sessionId,
  })
}

export function deleteFile(filename: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'deleteFile',
    filename: filename,
    session: sessionId,
  })
}

export function renameFile(oldname: string, newname: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'renameFile',
    oldname: oldname,
    newname: newname,
    session: sessionId,
  })
}

export function uploadFileChunk(
  filename: string,
  data: string,
  hash: string,
  chunkIndex: number,
  totalChunks: number,
  sessionId: string,
): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'uploadFile',
    filename: filename,
    data: data,
    hash: hash,
    chunkIndex: chunkIndex,
    totalChunks: totalChunks,
    session: sessionId,
    ackRequired: true,
  })
}

export function login(username: string, password: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'login',
    user: username,
    password: password,
    session: sessionId,
    ackRequired: true,
  })
}

export function logout(sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'logout',
    session: sessionId,
    ackRequired: true,
  })
}

export function requestFileDownload(filename: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'downloadFile',
    filename: filename,
    session: sessionId,
  })
}

// Admin system management functions
export function rebootDevice(): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'reboot',
  })
}

export function restartApp(): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'restart',
  })
}

export function updateApp(url: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'update',
    url: url,
  })
}

export function requestScreenshot(pageId: string): void {
  isLoadingScreenshot.value = true
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'getScreen',
    value: pageId,
  })
}

export function requestFileUpload(
  downloadLink: string,
  filename: string,
  hash: string,
  sessionId: string,
): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'uploadFile',
    download_link: downloadLink,
    filename: filename,
    hash: hash,
    session: sessionId,
  })
}

export interface IDmxState {
  color: number
  effect: number
  speed: number
  brightness: number
  effectList?: string[]
  id?: string
  session?: string
}

export const currentDmx = ref({} as IDmxState)

export function getDmx(id: string, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'getDmx',
    session: sessionId,
    id: id,
  })
}

export function setDmx(id: string, sessionId: string, data: IDmxState): void {
  console.log('Setting DMX:', data)
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'setDmx',
    session: sessionId,
    id: id,
    data: {
      color: data.color,
      effect: data.effect,
      speed: data.speed,
      brightness: data.brightness,
    },
  })
}

export function requestTrendData(
  datapoints: string[],
  start: number,
  end: number,
  sessionId: string,
): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'getTrend',
    datapoints,
    start,
    end,
    session: sessionId,
  })
}

// Pot calibration command
export function setPotCalibration(
  screenName: string,
  calibrateValue?: number,
  calibrateVoltage?: number,
  reset?: boolean,
): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'setCalibration',
    id: screenName,
    data: {
      calibrateValue,
      calibrateVoltage,
      reset,
    },
  })
}

// Request calibration archive data
export function requestCalibrationArchive(screenId: string, sessionId?: string): void {
  const mqttStore = useMqttStore()
  const payload: Record<string, unknown> = {
    cmd: 'getCalArchive',
    id: screenId,
  }

  if (sessionId) {
    payload.session = sessionId
  }

  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, payload)
}

export function requestOrder(sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'getOrder',
    session: sessionId,
  })
}

export function submitOrder(orderData: Record<string, number>, sessionId: string): void {
  const mqttStore = useMqttStore()
  mqttStore.publish(`${getDeviceIdentifier()}/cmd`, {
    cmd: 'setOrder',
    data: orderData,
    session: sessionId,
  })
}
